import { Drawer, Box, Typography, Button } from '@mui/material'
import TablePagination from 'components/TablePagination'
import RefreshIcon from '@mui/icons-material/Refresh'
import TokenIcon from '@mui/icons-material/Token'
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown'
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp'
import { useDispatch, useSelector } from 'react-redux'
import { RootState } from 'redux/store'
import { useEffect, useState, useMemo } from 'react'
import styles from './NotificationDrawer.module.sass'
import { getTransfers } from 'services/portfolio'
import { useAuth } from 'react-oidc-context'
import Spinner from 'components/Spinner'

const NotificationDrawer = ({ isOpenNotificationDrawer, toggleDrawer }) => {
  const auth = useAuth();
  const [page, setPage] = useState(1)
  const [expandedCard, setExpandedCard] = useState(null)
  const [transferData, setTransferData] = useState({})
  const [isLoading, setIsLoading] = useState(false)
  
  const dispatch = useDispatch()
  const rowsPerPage = 10

  const portfolioHoldings = useSelector((state: RootState) => state.portfolio.holdings)
  const portfolioTokenAddresses = useMemo(() => portfolioHoldings?.map(h => h.token_address) || [], [portfolioHoldings])

  const formatDate = (timestamp) => {
    try {
      return new Date(parseInt(timestamp) * 1000).toLocaleString()
    } catch {
      return 'Invalid Date'
    }
  }

  useEffect(() => {
    if (isOpenNotificationDrawer) {
      setPage(1)
      fetchTransferData()
    }
  }, [isOpenNotificationDrawer])

  const processedNotifications = useMemo(() => {
    const aggregatedData = []
    
    Object.entries(transferData).forEach(([tokenAddress, transfers]) => {
      const tokenInfo = portfolioHoldings?.find(h => h.token_address === tokenAddress)
      
      if (tokenInfo && Array.isArray(transfers) && transfers.length > 0) {
        const sortedTransfers = [...transfers].sort((a, b) => parseInt(b.timestamp) - parseInt(a.timestamp))
        const totalVolume = transfers.reduce((sum, t) => {
          const value = parseFloat(t.value || 0)
          return isNaN(value) ? sum : sum + value
        }, 0)

        const uniqueAddresses = new Set([
          ...transfers.map(t => t.from_address),
          ...transfers.map(t => t.to_address)
        ])

        const latestTransfer = sortedTransfers[0]
        const oldestTransfer = sortedTransfers[sortedTransfers.length - 1]

        aggregatedData.push({
          token_address: tokenAddress,
          token_name: tokenInfo.name,
          token_symbol: tokenInfo.symbol,
          total_transfers: transfers.length,
          total_volume: totalVolume.toLocaleString(undefined, { maximumFractionDigits: 2 }),
          unique_addresses: uniqueAddresses.size,
          latest_transfer_time: latestTransfer.timestamp,
          latest_from: latestTransfer.from_address,
          latest_to: latestTransfer.to_address,
          latest_value: latestTransfer.value,
          latest_hash: latestTransfer.transaction_hash,
          transfer_period: `${formatDate(oldestTransfer.timestamp)} - ${formatDate(latestTransfer.timestamp)}`
        })
      }
    })
    
    return aggregatedData.sort((a, b) => parseInt(b.latest_transfer_time) - parseInt(a.latest_transfer_time))
  }, [transferData, portfolioHoldings])

  const paginatedData = processedNotifications?.slice((page - 1) * rowsPerPage, page * rowsPerPage)

  const fetchTransferData = async () => {
    setIsLoading(true)
    try {
      if (!portfolioTokenAddresses?.length) return
      const data = await getTransfers(auth.user.access_token, portfolioTokenAddresses)
      const organizedData = (Array.isArray(data) ? data : []).reduce((acc, transfer) => {
        if (transfer?.token_address) {
          acc[transfer.token_address] = [...(acc[transfer.token_address] || []), transfer]
        }
        return acc
      }, {})
      setTransferData(organizedData)
    } catch (error) {
      console.error('Error fetching transfer data')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Drawer
      className={styles.drawer}
      classes={{ paper: styles.paper }}
      open={isOpenNotificationDrawer}
      onClose={toggleDrawer}
      anchor="right"
    >
      <div className={styles.content}>
        <div className={styles.header}>
          <Typography variant="h6">Token Transfer Summary</Typography>
          <button
            className={styles.refreshIcon}
            onClick={fetchTransferData}
            disabled={isLoading}
          >
            <RefreshIcon size={14} />
          </button>
        </div>

        <div className={styles.statsRow}>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{portfolioTokenAddresses?.length || 0}</div>
            <div className={styles.statLabel}>Portfolio Tokens</div>
          </div>
          <div className={styles.statCard}>
            <div className={styles.statValue}>{processedNotifications?.length || 0}</div>
            <div className={styles.statLabel}>Tracked Tokens</div>
          </div>
        </div>

        <div className={styles.dataContent}>
          {paginatedData?.length > 0 ? (
            paginatedData.map((token, index) => (
              <div key={token.token_address} className={styles.tokenCard}>
                <div 
                  className={styles.cardHeader}
                  onClick={() => setExpandedCard(expandedCard === index ? null : index)}
                >
                  <div className={styles.tokenInfo}>
                    <div className={styles.tokenIcon}>
                      <TokenIcon />
                    </div>
                    <div>
                      <span className={styles.tokenName}>{token.token_name}</span>
                      <span className={styles.tokenSymbol}> ({token.token_symbol})</span>
                    </div>
                  </div>
                  {expandedCard === index ? <KeyboardArrowUpIcon className={styles.expandIcon} /> : 
                   <KeyboardArrowDownIcon className={styles.expandIcon} />}
                </div>
                
                {expandedCard === index && (
                  <div 
                    className={styles.cardContent}
                    data-expanded={expandedCard === index}
                  >
                    <div className={styles.metricsGrid}>
                      <div className={styles.metricBox}>
                        <div className={styles.metricValue}>
                          {token.total_transfers.toLocaleString()}
                        </div>
                        <div className={styles.metricLabel}>Transfers Logged</div>
                      </div>
                      <div className={styles.metricBox}>
                        <div className={styles.metricValue}>
                          {token.total_volume}
                        </div>
                        <div className={styles.metricLabel}>Total Volume</div>
                      </div>
                      <div className={styles.metricBox}>
                        <div className={styles.metricValue}>
                          {token.unique_addresses.toLocaleString()}
                        </div>
                        <div className={styles.metricLabel}>Unique Addresses</div>
                      </div>
                    </div>

                    <div className={styles.transferDetails}>
                      <div className={styles.detailsGrid}>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Period</span>
                          <span className={styles.detailValue}>{token.transfer_period}</span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Latest Transfer</span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>From</span>
                          <span className={styles.detailValue}>{token.latest_from}</span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>To</span>
                          <span className={styles.detailValue}>{token.latest_to}</span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Value</span>
                          <span className={styles.detailValue}>{token.latest_value} {token.token_symbol}</span>
                        </div>
                        <div className={styles.detailRow}>
                          <span className={styles.detailLabel}>Hash</span>
                          <span className={styles.detailValue}>{token.latest_hash}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))
          ) : (
            // <Typography className={styles.emptyState}>
            //   {isLoading ? 'Loading transfer data...' : 'No transfer data to display.'}
            // </Typography>
            <Typography className={styles.emptyState}>
              {isLoading ? <Spinner /> : 'No transfer data to display.'}
            </Typography>
          )}
        </div>

        {paginatedData?.length ? (
          <TablePagination
            className={styles.pagination}
            classes={{
              root: styles.paginationRoot,
              actions: styles.paginationActions
            }}
            rowsPerPage={rowsPerPage}
            page={page}
            dataLength={processedNotifications?.length}
            setPage={(newPage) => setPage(newPage)}
          />
        ) : null}
      </div>
    </Drawer>
  )
}

export default NotificationDrawer